import React from 'react';

// Reusable Legend component for different map variables
export interface LegendConfig {
  title: string;
  dataYear: string;
  colorScale: string[];
  breaks: number[];
  unit: string;
  tractCount: number;
  referenceValue?: { label: string; value: number };
}

const MapLegend: React.FC<LegendConfig> = ({
  title, 
  dataYear, 
  colorScale, 
  breaks, 
  unit, 
  tractCount, 
  referenceValue 
}) => {
  // Generate legend items dynamically based on breaks and colors
  const legendItems = breaks.slice(0, -1).map((breakValue, index) => {
    const nextBreak = breaks[index + 1];
    const color = colorScale[index];
    
    // Format numbers based on unit type
    const formatValue = (value: number) => {
      if (unit === ' MT CO₂e/HH') {
        return value.toFixed(1); // One decimal place for carbon footprint
      } else {
        return value.toLocaleString(); // Default formatting for other units
      }
    };
    
    let label = '';
    if (index === 0) {
      label = `Under ${formatValue(breakValue)}${unit}`;
    } else if (index === breaks.length - 2) {
      label = `${formatValue(breakValue)}${unit}+`;
    } else {
      label = `${formatValue(breakValue)} - ${formatValue(nextBreak)}${unit}`;
    }
    
    return { color, label };
  });
  
  return (
    <div 
      className="bg-white p-3 rounded shadow-sm border" 
      style={{ 
        minWidth: '200px',
        position: 'relative',
        zIndex: 50, /* Lower than navigation z-index: 100 */
        display: 'block',
        visibility: 'visible',
        opacity: 1
      }}
    >
        <div className="mb-2">
          <h4 className="text-sm font-semibold text-gray-800">{title}</h4>
          <p className="text-xs text-gray-600">{dataYear}</p>
        </div>
        
        {/* Color Scale with Labels */}
        <div className="mb-2">
          <div className="space-y-1">
            {legendItems.map((item, index) => (
              <div key={index} className="flex items-center">
                <div 
                  className="mr-2"
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    backgroundColor: item.color,
                    border: '1px solid #ccc',
                    borderRadius: '2px',
                    display: 'inline-block'
                  }}
                ></div>
                <span className="text-xs text-gray-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Additional Info */}
        <div className="pt-2 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            {referenceValue && (
              <div>{referenceValue.label}: {unit}{unit === ' MT CO₂e/HH' ? referenceValue.value.toFixed(1) : referenceValue.value.toLocaleString()} (2023 data)</div>
            )}
            <div>{tractCount.toLocaleString()} census tracts</div>
          </div>
        </div>
    </div>
  );
};

export default MapLegend;
